
// #############################################################

import { formatNumber } from "./index"; // Import helpers

// Helper to format percentage
const formatPercent = (val, total) => {
    if (!total || total === 0) return "0%";
    return `${((val / total) * 100).toFixed(0)}%`;
};

// Helper for Country Names
const getCountryName = (code) => {
    try {
        const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
        return regionNames.of(code) || code;
    } catch (e) {
        return code;
    }
};

export default function DemographicKeyMetrics({ profile }) {
    if (!profile) return [];

    // -------------------------------------------------------
    // 1. Unified Demographics Logic (FB, IG, YouTube)
    // -------------------------------------------------------
    let demoObj = null;

    // A. Check Latest (Root)
    if (profile.profileDemographics) {
        demoObj = profile.profileDemographics;
    } else if (profile.pageDemographics) {
        // Facebook Map
        demoObj = {
            audience_gender_age: profile.pageDemographics.page_fans_gender_age || {},
            audience_country: profile.pageDemographics.page_follows_country || {}
        };
    } else if (profile.channelDemographics?.ageGroup || profile.channelDemographics?.gender || profile.channelDemographics?.country) {
        // YouTube Map - Ensure at least one has keys if utilizing root
        const hasData =
            Object.keys(profile.channelDemographics.ageGroup || {}).length > 0 ||
            Object.keys(profile.channelDemographics.gender || {}).length > 0;

        if (hasData) {
            demoObj = {
                audience_age: profile.channelDemographics.ageGroup || {},
                audience_gender: profile.channelDemographics.gender || {},
                audience_country: profile.channelDemographics.country || {}
            };
        }
    }

    // B. Check History if Latest is Empty
    if (!demoObj && profile.data && profile.data.length > 0) {
        const sortedData = [...profile.data].sort((a, b) => new Date(b.date || b.datetime || 0) - new Date(a.date || a.datetime || 0));

        const latestItem = sortedData.find(item =>
            item.profileDemographics ||
            item.pageDemographics ||
            (item.channelDemographics &&
                (Object.keys(item.channelDemographics.ageGroup || {}).length > 0 ||
                    Object.keys(item.channelDemographics.gender || {}).length > 0))
        );

        if (latestItem) {
            if (latestItem.profileDemographics) {
                demoObj = latestItem.profileDemographics;
            } else if (latestItem.pageDemographics) {
                demoObj = {
                    audience_gender_age: latestItem.pageDemographics.page_fans_gender_age || {},
                    audience_country: latestItem.pageDemographics.page_follows_country || {}
                };
            } else if (latestItem.channelDemographics) {
                demoObj = {
                    audience_age: latestItem.channelDemographics.ageGroup || {},
                    audience_gender: latestItem.channelDemographics.gender || {},
                    audience_country: latestItem.channelDemographics.country || {}
                };
            }
        }
    }

    if (!demoObj) return [];

    // -------------------------------------------------------
    // Calculate Top Gender & Age
    // -------------------------------------------------------
    // -------------------------------------------------------
    const genderAge = demoObj.audience_gender_age || demoObj.audienceGenderAge || {};
    const audienceAge = demoObj.audience_age || demoObj.audienceAge || {};
    const audienceGender = demoObj.audience_gender || demoObj.audienceGender || {}; // Explicit Gender (YT)

    let totalAudience = 0;
    const genderCounts = { F: 0, M: 0, U: 0 };
    const ageCounts = {};

    // Helper to clean keys
    const cleanAgeKey = (k) => k.replace(/^age/i, "");

    // Path 1: Combined Gender-Age (IG, FB)
    if (Object.keys(genderAge).length > 0) {
        Object.entries(genderAge).forEach(([key, value]) => {
            totalAudience += value;

            const parts = key.includes('_') ? key.split('_') : key.split('.');
            let gender = 'U';
            let age = key;

            if (parts.length >= 2) {
                if (["F", "M", "U"].includes(parts[0])) {
                    gender = parts[0];
                    age = parts[1];
                } else if (["F", "M", "U"].includes(parts[1])) {
                    gender = parts[1];
                    age = parts[0];
                }
            }
            if (genderCounts[gender] !== undefined) genderCounts[gender] += value;

            age = cleanAgeKey(age);
            ageCounts[age] = (ageCounts[age] || 0) + value;
        });
    }
    // Path 2: Separated Age/Gender (YouTube)
    else {
        // Age
        if (Object.keys(audienceAge).length > 0) {
            Object.entries(audienceAge).forEach(([ageKey, value]) => {
                totalAudience += value;
                const age = cleanAgeKey(ageKey);
                ageCounts[age] = (ageCounts[age] || 0) + value;
            });
        }
        // Gender
        if (Object.keys(audienceGender).length > 0) {
            Object.entries(audienceGender).forEach(([genKey, value]) => {
                // Map YT keys: male, female, user_specified
                let standardizedKey = 'U';
                const lowerKey = genKey.toLowerCase();
                if (lowerKey === 'male' || lowerKey === 'm') standardizedKey = 'M';
                else if (lowerKey === 'female' || lowerKey === 'f') standardizedKey = 'F';

                genderCounts[standardizedKey] += value;
                // If only gender exists, totalAudience might need incrementing if age didn't exist?
                // Usually totalAudience is same across dimensions. Maximize it.
            });
            // Recalculate Total from Gender if Age was empty
            if (totalAudience === 0) {
                totalAudience = Object.values(genderCounts).reduce((a, b) => a + b, 0);
            }
        }
    }

    // Determine Top Gender
    let topGender = "N/A";
    let topGenderVal = 0;
    const genderLabels = { F: "Female", M: "Male", U: "Gender Undetermined" }; // ✅ Updated Label

    Object.entries(genderCounts).forEach(([gender, count]) => {
        if (count > topGenderVal) {
            topGenderVal = count;
            topGender = genderLabels[gender];
        }
    });

    // Determine Top Age
    let topAge = "N/A";
    let topAgeVal = 0;
    Object.entries(ageCounts).forEach(([age, count]) => {
        if (count > topAgeVal) {
            topAgeVal = count;
            topAge = age; // Already cleaned
        }
    });

    // -------------------------------------------------------
    // Calculate Top Country
    // -------------------------------------------------------
    const countryData = demoObj.audience_country || demoObj.audienceCountry || {};
    let topCountry = "N/A";
    let topCountryVal = 0;
    let totalCountryAudience = 0; // ✅ Re-add total for calculation

    Object.entries(countryData).forEach(([code, val]) => {
        totalCountryAudience += val;
        if (val > topCountryVal) {
            topCountryVal = val;
            topCountry = code;
        }
    });

    // -------------------------------------------------------
    // Build Cards
    // -------------------------------------------------------
    const cards = [];

    // Top Gender
    if (topGender !== "N/A" && totalAudience > 0) {
        cards.push({
            title: "Top Gender",
            color: "accent",
            icon: "wc", // Gender icon
            count: topGender,
            fullCount: topGenderVal,
            description: `Makes up ${formatPercent(topGenderVal, totalAudience)} of your audience.`,
        });
    }

    // Top Age Group
    if (topAge !== "N/A" && totalAudience > 0) {
        cards.push({
            title: "Top Age Group",
            color: "accent",
            icon: "cake", // Birthday/Age icon
            count: topAge,
            fullCount: topAgeVal,
            description: `Makes up ${formatPercent(topAgeVal, totalAudience)} of your audience.`,
        });
    }

    // Top Country (✅ NEW)
    if (topCountry !== "N/A" && totalCountryAudience > 0) {
        cards.push({
            title: "Top Country",
            color: "accent",
            icon: "public", // Globe icon
            count: getCountryName(topCountry),
            fullCount: topCountryVal,
            description: `Makes up ${formatPercent(topCountryVal, totalCountryAudience)} of your audience.`,
        });
    }

    return cards;
}
